// filepath: apps/web/src/features/admin/webhooks/queries.ts
//
// FR-UI-ADM-6 -- TanStack Query hooks for the webhook endpoint
// admin editor.
//
// All hooks include `tenantSlug` in the cache key as the second
// segment (INV-1 / FR-UI-CORE-1 pattern). Switching tenants does
// NOT leak the prior tenant's endpoint list -- the cache key
// changes, the query refetches.
//
// Mutations carry `idempotency_key: crypto.randomUUID()` (INV-3 /
// FR-UI-PROM-2 pattern) so retries are idempotent. PATCH / DELETE
// carry `If-Match: <version>` for INV-7 optimistic concurrency;
// a mismatch is mapped to VersionConflictError so the page can
// open the EndpointConflictDialog (FR-UI-CORE-6 refetch / merge /
// discard UX).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  CreateWebhookEndpointRequest,
  CreateWebhookEndpointResponse,
  PatchWebhookEndpointRequest,
  PromotionReviewSummary,
  VersionConflictError,
  WebhookEndpoint,
  StagingOnlyError,
} from "./types";

/**
 * Cache keys for the webhook endpoint admin editor. tenantSlug is
 * the 2nd segment (INV-1). Mirrors the FR-UI-ADM-1 adminKeys
 * shape; co-located here rather than in the admin/queries.ts
 * barrel so the webhook surface is self-contained.
 */
export const webhookEndpointKeys = {
  endpoints: (tenantSlug: string) =>
    ["admin", tenantSlug, "webhook-endpoints"] as const,
  pendingReviews: (tenantSlug: string) =>
    ["admin", tenantSlug, "pending-reviews", "webhook_endpoint"] as const,
};

interface ListResponse {
  endpoints: WebhookEndpoint[];
}

/**
 * useWebhookEndpoints -- GET /api/webhooks/endpoints
 *
 * Returns the typed union `EndpointFetchError` on failure; the
 * page maps each kind to its dedicated error component. The
 * `stale_version` kind is wired through `useState` on the page
 * side (the mutation sets it when the server returns 409).
 */
export function useWebhookEndpoints() {
  const { tenantSlug } = useTenant();
  return useQuery<ListResponse, ApiError>({
    queryKey: webhookEndpointKeys.endpoints(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/webhooks/endpoints");
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as ListResponse;
    },
  });
}

/**
 * useCreateWebhookEndpoint -- POST /api/webhooks/endpoints.
 *
 * Sends `If-Match: 1` (POST always bumps from version=1) and
 * `X-Idempotency-Key: crypto.randomUUID()`. On 201 returns the
 * CreateWebhookEndpointResponse (which carries `key_value` ONCE
 * -- INV-5). On 409 with ENDPOINT_VERSION_MISMATCH throws
 * VersionConflictError (the route does not currently use this
 * envelope for POST but the type is wired through for parity
 * with the FR-UI-CORE-6 contract). On 403 with ENV_PRODUCTION
 * throws StagingOnlyError so the page can render the dedicated
 * gate.
 */
export function useCreateWebhookEndpoint() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    CreateWebhookEndpointResponse,
    Error | VersionConflictError | StagingOnlyError,
    CreateWebhookEndpointRequest
  >({
    mutationFn: async (req) => {
      const res = await apiFetch("/api/webhooks/endpoints", {
        method: "POST",
        init: {
          headers: {
            "Content-Type": "application/json",
            "If-Match": "1",
          },
          body: JSON.stringify(req),
        },
        idempotencyKey: true,
      });
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (body.code === "ENV_PRODUCTION") {
          const err: StagingOnlyError = {
            kind: "staging_only",
            message:
              body.error ?? "webhook endpoint management is staging-only",
          };
          throw err;
        }
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_endpoint?: WebhookEndpoint;
        };
        if (body.code === "ENDPOINT_VERSION_MISMATCH") {
          const err: VersionConflictError = {
            kind: "version_conflict",
            currentVersion: body.current_version ?? 0,
            serverEndpoint: body.server_endpoint,
            message: body.error ?? "version_conflict",
          };
          throw err;
        }
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as CreateWebhookEndpointResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: webhookEndpointKeys.endpoints(tenantSlug),
      });
      queryClient.invalidateQueries({
        queryKey: webhookEndpointKeys.pendingReviews(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * usePatchWebhookEndpoint -- PATCH /api/webhooks/endpoints/{id}.
 *
 * Sends `If-Match: <version>` (INV-7 optimistic concurrency).
 * On 409 with ENDPOINT_VERSION_MISMATCH throws VersionConflictError
 * so the page can open the EndpointConflictDialog. On success
 * invalidates the list + pending-reviews caches.
 */
export function usePatchWebhookEndpoint() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    { endpoint: WebhookEndpoint },
    Error | VersionConflictError | StagingOnlyError,
    { id: string; version: number; status: "active" | "disabled" }
  >({
    mutationFn: async ({ id, version, status }) => {
      const res = await apiFetch(
        `/api/webhooks/endpoints/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          init: {
            headers: {
              "Content-Type": "application/json",
              "If-Match": String(version),
            },
            body: JSON.stringify({
              status,
              version,
            } satisfies PatchWebhookEndpointRequest),
          },
          idempotencyKey: true,
        },
      );
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (body.code === "ENV_PRODUCTION") {
          const err: StagingOnlyError = {
            kind: "staging_only",
            message:
              body.error ?? "webhook endpoint management is staging-only",
          };
          throw err;
        }
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
          server_endpoint?: WebhookEndpoint;
        };
        if (body.code === "ENDPOINT_VERSION_MISMATCH") {
          const err: VersionConflictError = {
            kind: "version_conflict",
            currentVersion: body.current_version ?? 0,
            serverEndpoint: body.server_endpoint,
            message: body.error ?? "version_conflict",
          };
          throw err;
        }
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
      return (await res.json()) as { endpoint: WebhookEndpoint };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: webhookEndpointKeys.endpoints(tenantSlug),
      });
      queryClient.invalidateQueries({
        queryKey: webhookEndpointKeys.pendingReviews(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * useDeleteWebhookEndpoint -- DELETE /api/webhooks/endpoints/{id}.
 *
 * Sends `If-Match: <version>` (INV-7). On 409 with
 * ENDPOINT_VERSION_MISMATCH throws VersionConflictError so the
 * page can open the EndpointConflictDialog. On success
 * invalidates the list + pending-reviews caches.
 */
export function useDeleteWebhookEndpoint() {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error | VersionConflictError | StagingOnlyError,
    { id: string; version: number }
  >({
    mutationFn: async ({ id, version }) => {
      const res = await apiFetch(
        `/api/webhooks/endpoints/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          init: {
            headers: { "If-Match": String(version) },
          },
          idempotencyKey: true,
        },
      );
      if (res.status === 403) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        if (body.code === "ENV_PRODUCTION") {
          const err: StagingOnlyError = {
            kind: "staging_only",
            message:
              body.error ?? "webhook endpoint management is staging-only",
          };
          throw err;
        }
      }
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          current_version?: number;
        };
        if (body.code === "ENDPOINT_VERSION_MISMATCH") {
          const err: VersionConflictError = {
            kind: "version_conflict",
            currentVersion: body.current_version ?? 0,
            message: body.error ?? "version_conflict",
          };
          throw err;
        }
      }
      if (!res.ok) {
        throw new ApiError(res.statusText, res.status, `http_${res.status}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: webhookEndpointKeys.endpoints(tenantSlug),
      });
      queryClient.invalidateQueries({
        queryKey: webhookEndpointKeys.pendingReviews(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * usePromotionPendingCount -- GET /api/admin/promotion-reviews
 *   ?def_type=webhook_endpoint&status=pending_review
 * Returns the count of pending reviews. Mirrors the FR-UI-ADM-1
 * usePromotionPendingCount.
 */
export function usePromotionPendingCount() {
  const { tenantSlug } = useTenant();
  return useQuery<number, ApiError>({
    queryKey: webhookEndpointKeys.pendingReviews(tenantSlug),
    queryFn: async () => {
      const url =
        "/api/admin/promotion-reviews?def_type=webhook_endpoint&status=pending_review";
      const r = await apiFetch(url);
      if (!r.ok) {
        if (r.status === 404) {
          return 0;
        }
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      const data = (await r.json()) as PromotionReviewSummary[];
      return data.length;
    },
  });
}
